import React from "react";
import IframeResizer from 'iframe-resizer-react'
import { IFrameObject } from 'iframe-resizer-react';
import Dimension from 'models/Dimension'
import AuthContext from 'store/AuthContext'
import SettingsService from 'services/SettingsService'
import NativeInterface from 'services/NativeInterface'
import Geolocation from 'models/Geolocation'
import GeolocationService from 'services/GeolocationService'
import { useLanguage } from './LanguageContext';
import { useSearchParams } from "react-router-dom"

const DimensionMain: React.FC<{dimension: Dimension, key: number}> = (props) => {
    const authCtx = React.useContext(AuthContext);
    const [iframeUrl, setIframeUrl] = React.useState('')
    const iframeRef = React.useRef<IFrameObject | null>(null);
    const { language, setLanguage } = useLanguage();
    const skipEffect = React.useRef(false);
  
    const [searchParams, setSearchParams] = useSearchParams();

    React.useEffect(() => {
        if(!skipEffect.current) {
            setIframeUrl(makeIframeUrl());
        }
        else {
            skipEffect.current = false;
        }
    }, [authCtx, props.dimension, language]);

    React.useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        for (const key of newParams.keys()) {
            if (key !== 'dim') {
                newParams.delete(key);
                changed = true;
            }
        }

        if (changed) {
            skipEffect.current = true;
            setSearchParams(newParams, { replace: true });
    }
    }, [searchParams]);

    React.useEffect(() => {
        const geolocationCallback = (loc: Geolocation | null) => {
            sendGeolocationUpdate(loc)
        }
        GeolocationService.subscribeOnUpdate(geolocationCallback)

        let webViewShowCallback: (() => void) | null = null;
        let webViewHideCallback: (() => void) | null = null;

        if (NativeInterface.isPWA()) {
            webViewShowCallback = () => {
                if (iframeRef.current) {
                    iframeRef.current.sendMessage({ type: 'WEBVIEW_SHOW' });
                }
            }

            webViewHideCallback = () => {
                if (iframeRef.current) {
                    iframeRef.current.sendMessage({ type: 'WEBVIEW_HIDE' });
                }
            }

            NativeInterface.subscribeOnEvent(NativeInterface.ON_WEBVIEW_SHOW, webViewShowCallback)
            NativeInterface.subscribeOnEvent(NativeInterface.ON_WEBVIEW_HIDE, webViewHideCallback)
        }


        return () => {
            GeolocationService.unsubscribeOnUpdate(geolocationCallback)
            if (NativeInterface.isPWA()) {
                NativeInterface.unsubscribeOnEvent(NativeInterface.ON_WEBVIEW_SHOW, webViewShowCallback)
                NativeInterface.unsubscribeOnEvent(NativeInterface.ON_WEBVIEW_HIDE, webViewHideCallback)
            }
        }
    }, [])

    React.useEffect(() => {
        if (iframeRef.current) {
            sendGeolocationUpdate(GeolocationService.location())
        }
    }, [iframeRef])

    function sendGeolocationUpdate(loc: Geolocation | null) {
        if (iframeRef.current && loc) {
            iframeRef.current.sendMessage({ type: 'GEOLOCATION_UPDATE', location: loc });
        }
    }

    function makeIframeUrl(): string {
        //const baseUrl = `https://web.dimx.world/1234567890123/env/dev/code/web/dist/index.html`
        const baseUrl = authCtx.makeFileUrl(props.dimension.id, 'code/web/dist/index.html')
        const postInfo = SettingsService.webGenericService();
        
        const lang = searchParams.get("lang");
        
        const params = new URLSearchParams({
            dim: props.dimension.id,
            acc: authCtx.account.id,
            email: authCtx.account.email,
            env: authCtx.account.getDimensionEnv(props.dimension.id),
            inst: NativeInterface.getAppInstanceId(),
            post: encodeURIComponent(postInfo.https_url),
            dummy: Math.random().toString(),
            lang: lang || language
        });

        for (const [key, value] of searchParams.entries()) {
            if (!params.has(key)) {
                params.append(key, value);
            }
        }

        if(lang){
            setLanguage(lang);
        }
       

        const url = `${baseUrl}?${params.toString()}`;
        console.log(`Iframe URL [${url}]`);
        return url;
    }

    if (!iframeUrl) {
        return null;
    }

    return (
        <div style={{ width: '100%', height: "100%", padding: "0", margin: "0"}}>
            <IframeResizer forwardRef={iframeRef} src={iframeUrl} scrolling = {true} style={{ width: '100%', height: "100%"}}/>
        </div>
    );
};

export default DimensionMain;
